import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import _groupBy from 'lodash/groupBy';
import moment from 'moment';
import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { flowNodeColor } from 'fable/flowEditor';
import { hexStringToNumber } from 'fable/flowEditor/components/flowManager/canvas/renderers/common/colors';
import { Text } from 'flow-builder/node/text';
import { TextBreak } from 'flow-builder/node/textBreak';
import { TextContent } from 'flow-builder/node/textContent';
import { createOpeningHoursModalContainer } from '../../../containers/modals/openingHours';
import { convertToHumanRelativeTime } from '../../../helpers/timeHelpers';
import { Day } from '../../types';
const translatePayload = (node) => {
    const { closedNodeId, openNodeId } = node.payload;
    return [
        {
            id: uuid(),
            connection: closedNodeId || uuid(),
            label: 'Closed',
        },
        {
            id: uuid(),
            connection: openNodeId || uuid(),
            label: 'Open',
        },
    ];
};
const updateConnection = (node, connection, connectionId) => {
    const entryName = connection.connectionLabel === 'Open' ? 'openNodeId' : 'closedNodeId';
    const payload = Object.assign(Object.assign({}, node.payload), { [entryName]: connectionId });
    return Object.assign(Object.assign({}, node), { payload });
};
export const createOpeningHoursNodeAttributes = (isDeprecated) => ({
    iconName: 'clock',
    label: 'Opening hours',
    color: hexStringToNumber(flowNodeColor.purple),
    attributes: {
        label: 'Opening...',
        isDeprecated,
    },
    helpText: 'By setting your opening hours, you can decide what happens to incoming conversations based on whether your business is open or closed.',
    translatePayload,
    updateConnection,
    NodeContent: OpeningHoursNode,
    ActionModal: createOpeningHoursModalContainer(isDeprecated),
});
export const OpeningHoursNodeAttributes = createOpeningHoursNodeAttributes(false);
export const OpeningHoursDeprecatedNodeAttributes = createOpeningHoursNodeAttributes(true);
const weekdays = moment
    .weekdays()
    .map((weekday) => Day[weekday]);
const getOpeningHours = (intervals) => {
    const [lastInterval] = intervals;
    const open = convertToHumanRelativeTime((lastInterval === null || lastInterval === void 0 ? void 0 : lastInterval.open) || '');
    const closed = convertToHumanRelativeTime((lastInterval === null || lastInterval === void 0 ? void 0 : lastInterval.close) || '');
    return lastInterval ? `${open} - ${closed}` : 'Closed';
};
export const getGroupedDays = (schedule) => {
    let index = 0;
    let currentKey = '';
    return _groupBy(schedule, (day) => {
        const intervalStr = getOpeningHours(day.intervals);
        const keyStr = `${index}-[${intervalStr}]`;
        if (keyStr !== currentKey) {
            index += 1;
            currentKey = `${index}-[${intervalStr}]`;
        }
        return currentKey;
    });
};
const useOpeningHours = (schedule) => {
    return useMemo(() => {
        const groupedDays = getGroupedDays(schedule);
        return Object.values(groupedDays).map((days) => {
            const [firstDay] = days;
            const dayNames = days.map(({ date }) => moment.utc(date, moment.ISO_8601).format('ddd'));
            const isOnlyWeekdays = dayNames.length === 5 &&
                weekdays.slice(1, -1).every((day, index) => day === dayNames[index]);
            const isOnlyWeekend = dayNames.length === 2 &&
                [Day.Saturday, Day.Sunday].every((day, index) => day === dayNames[index]);
            const day = {};
            if (isOnlyWeekdays) {
                day.name = 'Weekdays';
            }
            if (isOnlyWeekend) {
                day.name = 'Weekends';
            }
            if (!day.name) {
                day.name = dayNames.join(', ');
            }
            day.hours = getOpeningHours(firstDay.intervals);
            return day;
        });
    }, [schedule]);
};
export function OpeningHoursNode({ node }) {
    const { payload } = node;
    const schedule = useOpeningHours(payload.schedule);
    return (_jsx(TextContent, { children: useMemo(() => schedule.map(({ name, hours }) => (_jsxs(TextBreak, { children: [_jsx(Text, { weight: "normal", text: name }), _jsx(Text, { weight: "semibold", text: hours })] }, name))), [schedule]) }));
}
